/**
 * The customer's own "Request a Quote" journey — its three server calls.
 *
 * WHY A SEPARATE MODULE and not `services/quotes.ts`: everything here is
 * CUSTOMER-SCOPED and CREATE-ONLY. `quoteService` carries the broker's whole
 * write surface (share, unshare, submit, cancel, premium options); importing
 * it into the customer journey would put all of that one keystroke away from
 * a screen that is allowed exactly one write. The server refuses those calls
 * to a customer independently (`backend/tests/test_customer_portal_quotes.py`),
 * so this is the second lock, not the only one.
 *
 * The journey ENDS at `createQuoteRequest`. Stages 12-14 -- Broker Admin
 * Leads, verification, payment, issuance -- are the broker's existing flow,
 * and the created request lands in their Quote Requests inbox on its own
 * (`apps/quotes/enquiries.base_querysets` selects exactly
 * `created_by__role="customer"`, `is_shared=False`). Nothing here rebuilds
 * any of it.
 */

import api from './api';

/** One brokerage this login is a customer of, and whether it can price live. */
export interface BrokerPricingCapability {
  broker_id: string;
  broker_name: string;
  customer_id: string;
  /** True only when a real insurer integration would be reached. */
  live_pricing: boolean;
  /** The word the UI prints. Decided server-side so two screens cannot
   *  disagree about what happened. */
  pricing_path: 'live' | 'manual';
  insurers: { code: string; name: string }[];
  /** Plain-language reason, non-empty whenever `live_pricing` is false. */
  reason: string;
}

export interface PricingCapabilityResponse {
  insurance_type: string;
  product_line: string;
  environment: string;
  product_line_has_adapter: boolean;
  brokers: BrokerPricingCapability[];
  /** `no_brokerage_for_customer` when this login is on no brokerage's books. */
  code?: string;
  detail?: string;
}

/** One priced option the create call actually persisted. Never assembled
 *  client-side: the server reads these back from the saved rows. */
export interface JourneyPricedOption {
  quote_id: string;
  quote_number: string;
  insurer_name: string;
  insurer_code: string;
  total_premium: string;
  gross_premium: string;
  idv: string | null;
  valid_until: string | null;
}

/**
 * What the 201 says happened to the request — stage 8, told rather than
 * implied. `quotes_created` is a count of PERSISTED rows, so zero means zero;
 * `options` is read back from those rows, so no premium is ever invented.
 */
export interface JourneyPricingReport {
  pricing_path: 'live' | 'manual';
  status: 'priced' | 'manual' | 'no_adapter' | 'no_prices';
  quotes_created: number;
  options: JourneyPricedOption[];
  detail: string;
  product_line?: string;
  insurers_queried?: number;
  failures?: { insurer_code: string; error_code: string; message: string }[];
}

export interface CreatedQuoteRequest {
  id: string;
  request_number: string;
  pricing: JourneyPricingReport;
}

/** The body the journey posts. `broker` is only sent when the customer had to
 *  CHOOSE one; the server validates it against their own customer records and
 *  403s otherwise, so it is not a way to reach a stranger's book. */
export interface QuoteJourneySubmission {
  insurance_type: string;
  business_type: 'new' | 'renewal' | 'rollover';
  proposer_type: 'individual' | 'corporate';
  proposer_name: string;
  proposer_email?: string;
  proposer_phone?: string;
  proposer_pincode?: string;
  proposer_dob?: string;
  registration_number?: string;
  sum_insured?: string;
  policy_term_years?: number;
  cover_type?: string;
  requested_addons?: string[];
  notes?: string;
  source?: string;
  additional_data?: Record<string, unknown>;
  broker?: string;
}

/** The refusal shapes the create path answers with. Both carry a `code` the
 *  journey switches on — a bare message would leave it guessing. */
export interface QuoteJourneyRefusal {
  code?: string;
  detail?: string;
  brokers?: { broker_id: string; broker_name: string; customer_id: string }[];
}

/** Every non-empty value MUST be a `QuoteRequest.cover_type` choice
 *  (`backend/apps/quotes/models.py`): comprehensive / third_party /
 *  standalone_od / bundled. This list once sent the GoDigit adapter's tokens
 *  (`tp_only`, `saod`) -- the broker wizard's `product_variant` vocabulary,
 *  which the integrations submit view translates but `POST /quotes/requests/`
 *  does not -- so those two choices 400ed (B-02). `''` is "let my advisor
 *  recommend" and is sent as absent. Pinned by `customerQuoteJourney.choices.test.ts`. */
export const JOURNEY_COVER_OPTIONS = [
  { value: '', label: 'Let my advisor recommend' },
  { value: 'comprehensive', label: 'Comprehensive' },
  { value: 'third_party', label: 'Third Party Only' },
  { value: 'standalone_od', label: 'Standalone Own Damage' },
];

/** `QuoteRequest.source` for this journey. A real choice on the model since
 *  B-02 (migration quotes 0016); before that every submit 400ed on it. */
export const JOURNEY_SOURCE = 'customer_portal';

/**
 * STAGE 6 — the documents a customer may attach to their OWN request.
 *
 * The list MIRRORS `QuoteDocumentViewSet.CUSTOMER_UPLOAD_TYPES` server-side,
 * and the server is the authority: `quote_pdf`, `comparison_pdf` and
 * `policy_document` are refused there with a 403 whatever this file says. They
 * are missing here for the same reason they are missing there — the first two
 * are the broker's own output on the public link, and attaching the third MINTS
 * A POLICY on the broker's book.
 */
export const JOURNEY_DOCUMENT_TYPES = [
  { value: 'vehicle_rc', label: 'Vehicle RC' },
  { value: 'previous_policy', label: 'Previous policy' },
  { value: 'id_proof', label: 'ID proof (PAN, Aadhaar, passport)' },
  { value: 'address_proof', label: 'Address proof' },
  { value: 'kyc_document', label: 'Something else' },
];

/** The same ceiling and extension set `apps/customers/kyc.py` enforces. Checked
 *  here only so the customer is told BEFORE a 10 MB upload crosses a phone
 *  connection; the server check is the one that counts. */
export const JOURNEY_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const JOURNEY_ALLOWED_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
  '.tif',
  '.tiff',
];

/** A file the customer has CHOSEN. Nothing is on the server yet — the journey
 *  stages attachments locally and commits them only once the request itself
 *  has been created, which is what makes "Remove" possible without a delete
 *  endpoint (the customer has no DELETE on `/quotes/documents/`, by design). */
export interface JourneyAttachment {
  /** Client-side only, so an outcome can be matched back to its file. */
  id: string;
  file: File;
  documentType: string;
}

/** What the SERVER said happened to one attachment. `uploaded` is set only
 *  when the response carried a document id — an upload reported as succeeded
 *  with nothing behind it is this platform's signature defect. */
export interface JourneyAttachmentOutcome {
  attachmentId: string;
  name: string;
  documentType: string;
  status: 'uploaded' | 'failed';
  documentId?: string;
  detail?: string;
}

/** Client-side pre-check. Returns a message when the file will not be accepted. */
export function journeyUploadRefusal(file: File): string | null {
  if (!file.size) return 'That file is empty.';
  if (file.size > JOURNEY_MAX_UPLOAD_BYTES) {
    return `That file is larger than ${JOURNEY_MAX_UPLOAD_BYTES / 1048576} MB.`;
  }
  const dot = file.name.lastIndexOf('.');
  const extension = dot === -1 ? '' : file.name.slice(dot).toLowerCase();
  if (!JOURNEY_ALLOWED_EXTENSIONS.includes(extension)) {
    return 'Upload a PDF or a photo (JPG, PNG, WEBP, HEIC or TIFF).';
  }
  return null;
}

export const NO_BROKERAGE_CODE = 'no_brokerage_for_customer';
export const BROKER_CHOICE_REQUIRED_CODE = 'broker_choice_required';

export const customerQuoteJourney = {
  /** Read-only. Asked BEFORE stage 8 renders, so the screen never shows a
   *  spinner implying a live quote that cannot happen. */
  async pricingCapability(insuranceType: string): Promise<PricingCapabilityResponse> {
    const response = await api.get('/quotes/requests/pricing-capability/', {
      params: { insurance_type: insuranceType },
    });
    return response.data;
  },

  /** The insurers and plans this customer's brokerage is approved to sell.
   *  `CustomerCatalogView`, which already scopes itself to the caller's own
   *  `Customer` rows — stages 1 and 2 are served by it, not by anything new. */
  async catalog(insuranceType: string) {
    const response = await api.get('/products/catalog/', {
      params: { type: insuranceType },
    });
    return response.data as {
      insurance_type: string;
      insurers: {
        insurer_id: string;
        insurer_name: string;
        insurer_logo: string | null;
        claim_settlement_ratio: number | null;
        products: { id: string; name: string; description: string }[];
      }[];
    };
  },

  /** THE ONE WRITE. */
  async createQuoteRequest(body: QuoteJourneySubmission): Promise<CreatedQuoteRequest> {
    const response = await api.post('/quotes/requests/', body);
    return response.data;
  },

  /**
   * STAGE 6 — attach one staged file to a request that already exists.
   *
   * NEVER THROWS. The quote request is created first and is already in the
   * broker's inbox by the time this runs, so an upload failure is not a failed
   * request; reporting it as one is what makes a customer press Send again and
   * put a duplicate lead in that inbox. The outcome is returned instead, and
   * the screen states it.
   *
   * `uploaded` requires a document id in the response. A 2xx with no id is
   * reported as a FAILURE, because "we saved it" with nothing stored is the
   * defect this whole batch exists to remove.
   */
  async uploadDocument(
    quoteRequestId: string,
    attachment: JourneyAttachment
  ): Promise<JourneyAttachmentOutcome> {
    const base = {
      attachmentId: attachment.id,
      name: attachment.file.name,
      documentType: attachment.documentType,
    };
    const refusal = journeyUploadRefusal(attachment.file);
    if (refusal) return { ...base, status: 'failed', detail: refusal };

    const form = new FormData();
    form.append('quote_request', quoteRequestId);
    form.append('document_type', attachment.documentType);
    form.append('file', attachment.file);
    form.append('name', attachment.file.name);

    try {
      const response = await api.post('/quotes/documents/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // A phone photo on a slow uplink; the 30 s client default is too tight.
        timeout: 120000,
      });
      const documentId = response?.data?.id;
      if (!documentId) {
        return {
          ...base,
          status: 'failed',
          detail: 'The server did not confirm this file was stored.',
        };
      }
      return { ...base, status: 'uploaded', documentId: String(documentId) };
    } catch (error) {
      const data = (error as { response?: { data?: unknown } })?.response?.data as
        | { detail?: string; error?: { message?: string }; file?: string[] }
        | undefined;
      const detail =
        data?.error?.message ||
        data?.detail ||
        (Array.isArray(data?.file) ? data?.file[0] : undefined) ||
        'This file was not attached.';
      return { ...base, status: 'failed', detail };
    }
  },

  /** One at a time, deliberately: the broker's box is shared and a customer
   *  attaching five phone photos at once is the shape that times out. */
  async uploadDocuments(
    quoteRequestId: string,
    attachments: JourneyAttachment[]
  ): Promise<JourneyAttachmentOutcome[]> {
    const outcomes: JourneyAttachmentOutcome[] = [];
    for (const attachment of attachments) {
      // eslint-disable-next-line no-await-in-loop
      outcomes.push(await customerQuoteJourney.uploadDocument(quoteRequestId, attachment));
    }
    return outcomes;
  },
};
